import { Box, Button, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { Edit } from "@mui/icons-material";
import { EditUserDialog } from "./Components";
import useViewModel from "./ViewModel";
import { UserEntity } from "../../api/Users/Entity/UserEntity";
import { FormState, UserInterfaceState } from "../../shared/Enum";
import LoadingBackdrop from "../../shared/Components/LoadingBackdrop";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectProfile } from "../../store/profile/selector";
import PageContentHeader from "../../shared/Components/PageContentHeader";
import { enqueueSnackbar } from "notistack";

const DEFAULT_USER: UserEntity = {
  binusianId: "",
  businessUnit: "",
  email: "",
  name: "",
  roleId: "",
  title: "",
  status: false,
};

export function MasterUserPage() {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedData, setSelectedData] = useState<UserEntity>(DEFAULT_USER);
  const [filter, setFilter] = useState("");

  const { data, method } = useViewModel();
  const navigate = useNavigate();
  const profile = useSelector(selectProfile);

  useEffect(() => {
    method.onGetRoleList();
  }, []);

  useEffect(() => {
    method.onGetUserList(filter);
  }, [filter]);

  useEffect(() => {
    if (selectedData.binusianId != "") {
      setOpenDialog(true);
    }
  }, [selectedData]);

  useEffect(() => {
    if (
      UserInterfaceState.isError(data.uiState) ||
      FormState.isError(data.formState)
    ) {
      enqueueSnackbar({
        message:
          "Oops, something went wrong. Please try again later. If the issue persists, try refreshing the page or restarting the app.",
        variant: "error",
      });
    }
  }, [data.uiState, data.formState]);

  useEffect(() => {
    if (FormState.isSuccess(data.formState)) {
      if (selectedData.binusianId == profile.binusianId) {
        navigate("/");
      }
      setSelectedData(DEFAULT_USER);
      setFilter("");
    }
  }, [data.formState]);
  /* eslint-disable no-restricted-globals */
  /* eslint-disable unicorn/no-useless-undefined */

  const columns: GridColDef[] = [
    {
      field: "binusianId",
      renderHeader: () => (
        <Typography className="font-bold p-3 text-lg">Binusian ID</Typography>
      ),
      renderCell: (e) => (
        <Typography className="p-3">{e.row.binusianId}</Typography>
      ),
      width: 150,
    },
    {
      field: "name",
      renderHeader: () => (
        <Typography className="font-bold p-3 text-lg">Name</Typography>
      ),
      renderCell: (e) => (
        <Typography className="p-3 truncate">{e.row.name}</Typography>
      ),
      width: 300,
    },
    {
      field: "email",
      renderHeader: () => (
        <Typography className="font-bold p-3 text-lg">Email</Typography>
      ),
      renderCell: (e) => <Typography className="p-3">{e.row.email}</Typography>,
      width: 300,
    },
    {
      field: "businessUnit",
      renderHeader: () => (
        <Typography className="font-bold p-3 text-lg">Business Unit</Typography>
      ),
      renderCell: (e) => (
        <Typography className="p-3">{e.row.businessUnit}</Typography>
      ),
      width: 300,
    },
    {
      field: "title",
      renderHeader: () => (
        <Typography className="font-bold p-3 text-lg">Title</Typography>
      ),
      renderCell: (e) => <Typography className="p-3">{e.row.title}</Typography>,
      width: 300,
    },
    {
      field: "roleName",
      renderHeader: () => (
        <Typography className="font-bold p-3 text-lg">Role Name</Typography>
      ),
      renderCell: (e) => {
        const roleMap: Record<string, string> = {};
        data.roleList.forEach((role) => {
          roleMap[role.id] = role.name;
        });
        return <Typography className="p-3">{roleMap[e.row.roleId]}</Typography>;
      },
      width: 200,
    },
    {
      field: "status",
      renderHeader: () => (
        <Typography className="font-bold p-3 text-lg">Status</Typography>
      ),
      renderCell: (e) => (
        <Typography className="p-3">
          {e.row.status ? "Active" : "Inactive"}
        </Typography>
      ),
    },
    {
      field: "action",
      renderHeader: () => (
        <Typography className="font-bold p-3 text-lg">Action</Typography>
      ),
      renderCell: (e) => (
        <Box className="flex">
          <Button
            variant="contained"
            className="mt-1"
            onClick={() => setSelectedData(e.row)}
          >
            <Edit />
          </Button>
        </Box>
      ),
      width: 100,
    },
  ];

  return (
    <Box className="flex flex-col w-full p-3 md:p-5">
      <PageContentHeader label="User Management" />
      <Box className="flex justify-end md:p-3 w-full">
        <TextField
          id="filled-basic"
          label="Search"
          variant="outlined"
          className="bg-white w-full md:w-auto"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </Box>
      <div className="bg-white mt-3 md:m-3 border border-gray-400">
        <DataGrid
          rows={data.userList}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          getRowId={(row) => row.binusianId}
          pageSizeOptions={[5, 10]}
          autoHeight
          getRowHeight={() => "auto"}
        />
      </div>
      <EditUserDialog
        isOpen={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedData(DEFAULT_USER);
        }}
        onSubmit={(e) => {
          method.onUpdateUser(e);
          setOpenDialog(false);
        }}
        defaultValue={selectedData}
        roleList={data.roleList}
      />
      <LoadingBackdrop
        isOpen={
          UserInterfaceState.isLoading(data.uiState) ||
          FormState.isLoading(data.formState)
        }
      />
    </Box>
  );
}
